import { useState, useEffect } from "react";
import { callPymbleApi } from "./usePymbleApi";
import store from "../services/localStorageService";
import httpService from "../services/httpService";
import { api_s2 } from "../config";
import { IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";

const STORAGE_CONTAINER_SAS = "plc-storage-sas";
const EXPIRE_OFFSET_SECONDS = 300;

/**
 * Interfaces
 *
 * Expected format of response from StorageContainerSas
 */
export interface StorageContainerSasResponse {
  ContainerUrl?: string;
  SasExpiry?: string;
  SasQueryString?: string;
}
/* Format of final storage detail result */
export interface StorageContainerSasJson extends StorageContainerSasResponse {
  Url: string;
}

export type StorageEndpointType = {
  profile: string;
  secure: string;
};

/* Allowed endpoints */
export const StorageEndpoint: StorageEndpointType = {
  // profile: `${api.myPymble}/sas/profile-image`,
  // secure: `${api.myPymble}/sas/secure`,
  profile: "/storage/sas/?container=profile-images&expiry=1440",
  secure: "/storage/sas/?container=secure&expiry=1440",
};

/**
 * Convenience function to generate a key for local storage SAS requests
 */
function getSasEndpointStorageKey(endpoint: string) {
  return `${STORAGE_CONTAINER_SAS}-${endpoint}`;
}

function now() {
  return Math.floor(new Date().getTime() / 1000);
}

/**
 * Call API endpoing to generate a SAS query string required to retrieve
 * a file from a secure storage container in Azure.  Eg:
 * https://pymblelc.blob.core.windows.net/secure/secure/
 * https://pymblelc.blob.core.windows.net/secure/profile-images/
 *
 * Logic lifted from PymbleLCAzureAPIM library found here:
 * https://github.com/Pymble-Ladies-College/datatown-apim-js/
 *
 * @param {string} endpoint  The url endpoint path that generates the SAS query string
 * @returns <StorageContainerSasResponse> The SAS object containing the token
 */
const requestStorageContainerSas = async (
  instance: IPublicClientApplication,
  endpoint: string
) => {
  // console.debug("requestStorageContainerSas()");

  let token: StorageContainerSasResponse = {};

  try {
    // Construct a key for this endpoint
    const storeKey = getSasEndpointStorageKey(endpoint);

    // Call API to generate the SAS
    // token = await callPymbleApi(instance, "GET", endpoint);
    token = await callPymbleApi(
      instance,
      "GET",
      endpoint,
      {
        baseURL: api_s2.baseURL,
        headers: {
          Accept: "application/json",
          PymbleClientId: "covers-manager",
        },
      },
    );

    if (token && token.SasQueryString) {
      await store.setItem(storeKey, JSON.stringify(token));
      let SasExpiry = 0;
      if (token.SasExpiry) {
        SasExpiry = Math.floor(Date.parse(token.SasExpiry) / 1000);
      }
      await store.setItem(`${storeKey}-expires`, SasExpiry.toString());
    } else {
      throw new Error(
        "requestStorageContainerSas: shared access signature request failed."
      );
    }
  } catch (e: any) {
    console.error("Could not call storage sas api.", e.message);
  }
  return token;
};

/**
 * Get the full File access url with correct SAS query string appended,
 * so a file download / access can be made to the Azure storage container.
 *
 * @param {string} endpoint   Endpoint for SAS generation (eg /secure/sas | /profile-image/sas )
 * @param {string} fileRelativePath  Relative path + name of file
 * @param {boolean} checkExists  Perform a HEAD check on resource to validate it.
 *                               If HEAD check fails, then url returned will be blank
 * @returns Object containing to access details to the file:
 * {
    Url: string;
    ContainerUrl: string;
    SasQueryString: string;
    SasExpiry: string;
   } 
 */
export const getStorageContainerJson = async (
  instance: IPublicClientApplication,
  endpoint: string,
  fileRelativePath: string = "",
  checkExists: boolean = false
) => {
  // console.debug("useAzureStorage.getStorageContainerJson()");

  let json: StorageContainerSasJson = {
    Url: "",
    ContainerUrl: "",
    SasQueryString: "",
    SasExpiry: "",
  };

  if (fileRelativePath) {
    try {
      // console.debug("useAzureStorage.getStorageContainerUrl()");
      const token = await checkStorageContainerSas(instance, endpoint);
      if (token) {
        json = {
          Url: `${token.ContainerUrl}/${fileRelativePath}?${token.SasQueryString}`,
          ...token,
        };

        if (checkExists) {
          // Check file existence via head request
          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const res = await httpService.head(json.Url);
            // console.debug("response from HEAD request:", JSON.stringify(res));
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (er) {
            console.debug("Url invalid. HEAD request failed.");
            json.Url = "";
          }
        }
      } else {
        console.error("No storage shared access signature available.");
      }
    } catch (e: any) {
      const err = `Could not retrieve storage container url. ${e.message}`;
      console.error(err);
    }
  }
  return json;
};

/**
 * Check if we have a valid SAS token that has not expired.
 * Tokens are generated and stored based on the value of
 * endpoint passed as a parameter to this function.
 *
 * @param {string} endpoint
 * @returns
 */
const checkStorageContainerSas = async (
  instance: IPublicClientApplication,
  endpoint: string
) => {
  // console.debug("useAzureStorage.checkStorageContainerSas()");
  let sas: string | null = "";
  let sasParsed: StorageContainerSasResponse = {};

  try {
    // Construct a key for this endpoint
    const storeKey = getSasEndpointStorageKey(endpoint);

    // Check local storage for a cached SAS object
    sas = await store.getItem(storeKey);

    if (sas) {
      // Consider the signature expired expireOffsetSeconds before expiry
      let sasExpiresStr: string | null = await store.getItem(
        `${storeKey}-expires`
      );
      if (!sasExpiresStr) {
        sasExpiresStr = "60";
      }
      const sasExpires = parseInt(sasExpiresStr, 10);
      if (now() < sasExpires - EXPIRE_OFFSET_SECONDS) {
        sasParsed = JSON.parse(sas);
      } else {
        console.debug(
          "checkStorageContainerSas: the cached shared access signature has expired"
        );
        sasParsed = await requestStorageContainerSas(instance, endpoint);
      }
    } else {
      // No sas stored, make and store one
      sasParsed = await requestStorageContainerSas(instance, endpoint);
    }
  } catch (e) {
    console.error("Error checking storage container sas", JSON.stringify(e));
  }
  return sasParsed;
};

/**
 * Hook to call APIs to generate a Share Access Secret and url for secure storage retrieval
 * on Azure Storage endpoints.  Can be used for secure file locations or profile images.
 *
 * @param {string} storageEndpoint Endpoint for SAS generation (eg /secure/sas | /profile-image/sas )
 * @param {string} fileName Relative path + name of file. Optional.
 * @param {boolean} checkFileExists Perform a HEAD check on resource to validate it.
 *                                  If HEAD check fails, then url returned will be blank.
 *                                  Optional.
 *
 * @returns { storageDetail, azureStorageLoaded }
 */
export function useAzureStorage(
  storageEndpoint: string,
  fileName: string = "",
  checkFileExists: boolean = false
) {
  const [storageDetail, setStorageDetail] = useState<StorageContainerSasJson>();
  const [azureStorageLoaded, setAzureStorageLoaded] = useState(false);
  const { instance /*, inProgress */ } = useMsal();

  // Load the data
  const getData = async () => {
    setAzureStorageLoaded(false);
    try {
      const res: StorageContainerSasJson = await getStorageContainerJson(
        instance,
        storageEndpoint,
        fileName,
        checkFileExists
      );
      setStorageDetail(res);
    } catch (e: any) {
      console.error(`Could not retrieve profile image url. ${e?.message}`);
    } finally {
      setAzureStorageLoaded(true);
    }
  };

  // Fires on load of hook
  useEffect(() => {
    getData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName]);

  return {
    storageDetail,
    azureStorageLoaded,
    getStorageContainerJson,
  };
}
